import { useEffect, useMemo, useState } from "react";
import {
  SHOP_TIME_ZONE,
  zonedDayKey,
  type AvailabilityDay,
  type Slot,
} from "@shared/modules/x-site";
import { Button } from "../../../core/components/ui/button";
import { Icon } from "../../../core/components/ui/icon";
import { getAgendaAvailability, getAgendaSlots } from "../lib/api";

// Sélecteur de créneaux du CRM : mêmes règles que le site et Léo (heures,
// pause de midi, fermetures, présence et occupation des techniciens, capacité,
// rendez-vous de toute provenance). Une indisponibilité interne fait
// simplement disparaître les créneaux ; la raison n'est jamais affichée ici.

const DAYS_PER_PAGE = 14;

function addDays(key: string, days: number): string {
  const t = new Date(`${key}T12:00:00Z`);
  t.setUTCDate(t.getUTCDate() + days);
  return t.toISOString().slice(0, 10);
}

const dayFormat = new Intl.DateTimeFormat("fr-CA", {
  timeZone: SHOP_TIME_ZONE,
  weekday: "short",
  day: "numeric",
  month: "short",
});

function dayLabel(key: string): string {
  return dayFormat.format(new Date(`${key}T12:00:00Z`));
}

const hourFormat = new Intl.DateTimeFormat("fr-CA", {
  timeZone: SHOP_TIME_ZONE,
  hour: "numeric",
  hourCycle: "h23",
});

type Period = "matin" | "apres-midi";

/** Le matin finit à midi, à l'heure de l'atelier (« 08 h » en fr-CA : la partie « heure » seule). */
function periodOf(slot: Slot): Period {
  const hour = hourFormat.formatToParts(new Date(slot.startAt)).find((p) => p.type === "hour");
  return Number(hour?.value) < 12 ? "matin" : "apres-midi";
}

const PERIOD_LABELS: Record<Period, string> = { matin: "Matin", "apres-midi": "Après-midi" };

function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T12:00:00Z`) - Date.parse(`${from}T12:00:00Z`)) / 86_400_000);
}

export function SlotPicker({
  durationMin,
  technicianId,
  preference,
  preferredDate,
  preferredPeriod,
  selectedStart,
  onPick,
}: {
  durationMin: number;
  technicianId?: string;
  /** Préférence exprimée par le client (demande du site), rappelée au-dessus des jours. */
  preference?: string | null;
  /** Jour choisi par le client sur le site : le sélecteur s'y ouvre s'il est disponible. */
  preferredDate?: string | null;
  /** Moment choisi par le client : repéré parmi les créneaux du jour. */
  preferredPeriod?: Period | null;
  selectedStart?: string | null;
  onPick: (slot: Slot) => void;
}) {
  const today = useMemo(() => zonedDayKey(Date.now()), []);
  // Ouvert sur la quinzaine du jour souhaité par le client, s'il est à venir.
  const [page, setPage] = useState(() =>
    preferredDate && preferredDate >= today
      ? Math.floor(daysBetween(today, preferredDate) / DAYS_PER_PAGE)
      : 0,
  );
  const [days, setDays] = useState<AvailabilityDay[] | null>(null);
  const [date, setDate] = useState<string | null>(null);
  const [slots, setSlots] = useState<Slot[] | null>(null);
  const [failed, setFailed] = useState(false);
  const duration = Math.max(15, Math.round(durationMin));
  const from = addDays(today, page * DAYS_PER_PAGE);
  const to = addDays(from, DAYS_PER_PAGE - 1);

  useEffect(() => {
    let alive = true;
    setDays(null);
    setFailed(false);
    getAgendaAvailability(from, to, duration)
      .then((list) => {
        if (!alive) return;
        setDays(list);
        // Jour retenu : celui déjà choisi s'il reste ouvert, sinon celui que le
        // client a demandé, sinon le premier disponible.
        const open = (key: string | null | undefined) =>
          Boolean(key) && list.some((d) => d.date === key && d.status === "disponible");
        setDate((current) =>
          open(current)
            ? current
            : open(preferredDate)
              ? preferredDate!
              : (list.find((d) => d.status === "disponible")?.date ?? null),
        );
      })
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, [from, to, duration, preferredDate]);

  useEffect(() => {
    if (!date) {
      setSlots(null);
      return;
    }
    let alive = true;
    setSlots(null);
    getAgendaSlots(date, duration, technicianId || undefined)
      .then((list) => alive && setSlots(list))
      .catch(() => alive && setSlots([]));
    return () => {
      alive = false;
    };
  }, [date, duration, technicianId]);

  return (
    <div className="slot-picker">
      {preference ? (
        <p className="slot-picker__preference">
          <Icon name="message" size={16} />
          Préférence du client : <strong>{preference}</strong>
        </p>
      ) : null}
      <div className="slot-picker__nav">
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={page === 0}
          onClick={() => setPage((p) => Math.max(0, p - 1))}
        >
          ← Plus tôt
        </Button>
        <span className="slot-picker__range">
          Durée prévue : {Math.floor(duration / 60) ? `${Math.floor(duration / 60)} h ` : ""}
          {duration % 60 ? `${duration % 60} min` : ""}
        </span>
        <Button type="button" size="sm" variant="ghost" onClick={() => setPage((p) => p + 1)}>
          Plus tard →
        </Button>
      </div>
      {failed ? (
        <p className="slot-picker__empty">Disponibilités indisponibles pour le moment.</p>
      ) : !days ? (
        <p className="slot-picker__empty">Chargement des disponibilités…</p>
      ) : (
        <div className="slot-picker__days" role="group" aria-label="Jours">
          {days.map((d) => (
            <button
              key={d.date}
              type="button"
              className={`slot-picker__day slot-picker__day--${d.status}${d.date === date ? " is-on" : ""}`}
              disabled={d.status !== "disponible"}
              aria-pressed={d.date === date}
              onClick={() => setDate(d.date)}
            >
              <span className="slot-picker__day-name">{dayLabel(d.date)}</span>
              <span className="slot-picker__day-meta">
                {d.status === "disponible"
                  ? `${d.freeSlots} créneau${d.freeSlots > 1 ? "x" : ""}`
                  : d.status === "complet"
                    ? "Complet"
                    : d.status === "ferme"
                      ? "Fermé"
                      : "Passé"}
              </span>
            </button>
          ))}
        </div>
      )}
      {date ? (
        slots === null ? (
          <p className="slot-picker__empty">Chargement des créneaux…</p>
        ) : slots.length === 0 ? (
          <p className="slot-picker__empty">Aucun créneau ce jour-là pour cette durée.</p>
        ) : (
          <div className="slot-picker__periods">
            {(["matin", "apres-midi"] as const).map((period) => {
              const list = slots.filter((s) => periodOf(s) === period);
              if (!list.length) return null;
              const wanted =
                preferredPeriod === period && (!preferredDate || preferredDate === date);
              return (
                <div
                  key={period}
                  className={`slot-picker__period${wanted ? " is-wanted" : ""}`}
                  role="group"
                  aria-label={`${PERIOD_LABELS[period]} du ${dayLabel(date)}`}
                >
                  <span className="slot-picker__period-name">
                    {PERIOD_LABELS[period]}
                    {wanted ? <span className="slot-picker__wanted">souhaité</span> : null}
                  </span>
                  <div className="slot-picker__slots">
                    {list.map((s) => {
                      const on = selectedStart
                        ? new Date(selectedStart).getTime() === new Date(s.startAt).getTime()
                        : false;
                      return (
                        <button
                          key={s.startAt}
                          type="button"
                          className={`slot-picker__slot${on ? " is-on" : ""}`}
                          aria-pressed={on}
                          onClick={() => onPick(s)}
                        >
                          {s.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )
      ) : days && !failed ? (
        <p className="slot-picker__empty">Aucun jour disponible sur cette période.</p>
      ) : null}
    </div>
  );
}
